import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calendar, ChevronRight, CheckCircle2, Loader2, MapPin, ArrowLeft, Mail, Phone, UserCheck, AlertCircle, ShieldAlert } from "lucide-react";
import axios from "axios";
import { PUBLIC_API } from "../lib/api";
import { toast } from "sonner";
import TeroumaLogo from "../components/ui/TeroumaLogo";
import IdentityVerification from "../components/IdentityVerification";
import { chargerFicheProuvee } from "../lib/ficheProuvee";

const DEFAULT_TYPES = [
  { id: "petit",  label: "Petit Kiddoush",      price: 300,  desc: "Pour ~20 personnes · Boissons, cakes, poisson" },
  { id: "grand",  label: "Grand Kiddoush",       price: 600,  desc: "Pour ~50 personnes · Buffet complet" },
  { id: "extra",  label: "Kiddoush Prestige",    price: 1200, desc: "Sur mesure · Pour 100+ personnes" },
];

function isSaturday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr + "T12:00:00");
  return d.getDay() === 6;
}

function toLocalDateStr(d) {
  // Use local date components to avoid UTC timezone shift (e.g. Israel UTC+3)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nextSaturdays(n = 12) {
  const result = [];
  const now = new Date();
  let d = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // midnight local
  // Find next Saturday (day 6), then add 7 more days
  const daysUntilNextSat = (6 - d.getDay() + 7) % 7 || 7;
  d.setDate(d.getDate() + daysUntilNextSat + 7);
  for (let i = 0; i < n; i++) {
    result.push(toLocalDateStr(d));
    d = new Date(d);
    d.setDate(d.getDate() + 7);
  }
  return result;
}

export default function KiddoushPage() {
  const [searchParams] = useSearchParams();
  const preselectedOrgId = searchParams.get("org"); // ?org=orgId to skip step 1

  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [takenDates, setTakenDates] = useState([]); // [{desired_date, status, slot_type, custom_slot_id}]
  const [needsApproval, setNeedsApproval] = useState(false); // résa au-delà de 3 → validation admin
  const [kiddoushTypes, setKiddoushTypes] = useState(DEFAULT_TYPES);
  const [seoudaEnabled, setSeoudaEnabled] = useState(false);
  const [seoudaTypes, setSeoudaTypes] = useState([]);
  const [customSlots, setCustomSlots] = useState([]); // [{id, date, slot_type, label, description, suggested_amount, taken}]
  const [parashiot, setParashiot] = useState({}); // { "YYYY-MM-DD": "Berechit" }
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    desired_date: "", kiddoush_type: "petit", custom_amount: "",
    // Créneau : "kiddoush" (matin) ou "seouda_chlichit" (après-midi).
    slot_type: "kiddoush",
    // Si on réserve un créneau personnalisé (occasion spéciale créée par l'admin).
    custom_slot_id: null,
    occasion: "", dedication: "", guests_estimate: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [orgsError, setOrgsError] = useState(false);
  const [contactCheck, setContactCheck] = useState(null); // null | {found, missing_fields, ...}
  const [contactChecking, setContactChecking] = useState(false);
  // Drapeau COLLANT « un contact a été examiné » — voir CampaignPublicPage.
  const [contactExamined, setContactExamined] = useState(false);

  // Unified identity verification token (anti-usurpation)
  const [verificationToken, setVerificationToken] = useState("");
  const identityVerified = Boolean(verificationToken);

  useEffect(() => {
    axios.get(`${PUBLIC_API}/organizations/public/directory`)
      .then(r => {
        // Masque les synagogues dont l'owner a désactivé le module Kiddoush :
        // non réservables → on ne les propose pas (ni dans la liste, ni via ?org=).
        const list = (r.data?.organizations || []).filter(
          (o) => !(o.disabled_modules || []).includes("kiddoush")
        );
        setOrgs(list);
        // If ?org=orgId in URL, auto-select and skip to step 2
        if (preselectedOrgId) {
          const found = list.find(o => o.id === preselectedOrgId);
          if (found) { setSelectedOrg(found); setStep(2); }
        }
      })
      .catch(() => setOrgsError(true));

    // Fetch parasha for all upcoming Saturdays via Hebcal API
    const fetchParashiot = async () => {
      try {
        const sats = nextSaturdays(16);
        const start = sats[0];
        const end = sats[sats.length - 1];
        // s=on : parasha hebdomadaire · maj=on : grandes fêtes. Certains Chabbat
        // tombent un jour de fête (Roch Hachana, Souccot, Kippour…) et n'ont PAS
        // de parasha → on affiche alors le nom de la FÊTE (sinon la case restait vide).
        const url = `https://www.hebcal.com/hebcal/?v=1&cfg=json&s=on&maj=on&start=${start}&end=${end}&i=off&lg=fr`;
        const res = await axios.get(url);
        const items = res.data?.items || [];
        const par = {};   // parasha (prioritaire)
        const hol = {};   // fête (repli si pas de parasha ce Chabbat)
        for (const item of items) {
          if (!item.date) continue;
          const key = item.date.slice(0, 10);
          if (item.category === "parashat") {
            const frName = (item.title || "").replace(/^Parashat\s+|^Parachah?\s+|^Parsha\s+/i, "").trim();
            const heName = (item.hebrew || "").replace(/^פָּרָשַׁת\s+|^פרשת\s+/, "").trim();
            par[key] = { fr: frName, he: heName };
          } else if (item.category === "holiday" && !hol[key]) {
            hol[key] = { fr: (item.title || "").trim(), he: (item.hebrew || "").trim() };
          }
        }
        setParashiot({ ...hol, ...par }); // la parasha l'emporte sur la fête
      } catch { /* silent - parasha is decorative */ }
    };
    fetchParashiot();
  }, []);

  useEffect(() => {
    if (!selectedOrg) return;
    axios.get(`${PUBLIC_API}/kiddoush/availability/${selectedOrg.id}`).then(r => {
      setTakenDates(r.data?.taken || []);
      if (r.data?.config) {
        const types = Object.entries(r.data.config).map(([id, v]) => ({
          id, label: v.label, price: v.price, desc: v.description,
        }));
        if (types.length > 0) setKiddoushTypes(types);
      }
      // Séouda chlichit (créneau de l'après-midi) — formules séparées + toggle org.
      setSeoudaEnabled(!!r.data?.seouda_enabled);
      const scfg = r.data?.seouda_config || {};
      setSeoudaTypes(
        Object.entries(scfg).map(([id, v]) => ({ id, label: v.label, price: v.price, desc: v.description }))
      );
      // Créneaux personnalisés (occasions spéciales créées par l'admin).
      setCustomSlots(r.data?.custom_slots || []);
    }).catch(() => {});
  }, [selectedOrg]);

  const saturdays = nextSaturdays(16);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Créneaux : kiddoush (matin) / séouda (après-midi) / occasion spéciale ──
  const isCustom = !!form.custom_slot_id;
  const currentTypes = form.slot_type === "seouda_chlichit" ? seoudaTypes : kiddoushTypes;
  const slotNoun = form.slot_type === "seouda_chlichit" ? "séouda" : "Kiddoush";
  // Créneau (date + repas) déjà pris (hors occasion spéciale). Rétrocompat :
  // slot_type absent = kiddoush.
  const slotTakenFor = (d, st) => takenDates.some(
    (t) => t.desired_date === d && !t.custom_slot_id && (t.slot_type || "kiddoush") === st
  );
  // À l'étape Date : une date n'est indisponible que si TOUS les repas proposés
  // sont pris (séouda activée → il faut kiddoush ET séouda pris pour la griser).
  const dateUnavailable = (d) => seoudaEnabled
    ? (slotTakenFor(d, "kiddoush") && slotTakenFor(d, "seouda_chlichit"))
    : slotTakenFor(d, "kiddoush");
  // Choix du repas à l'étape Date / occasions (réinitialise la date).
  const selectSlotType = (st) => setForm((f) => ({
    ...f, slot_type: st, custom_slot_id: null, desired_date: "",
    kiddoush_type: st === "seouda_chlichit" ? (seoudaTypes[0]?.id || "libre") : "petit",
    custom_amount: "",
  }));
  // Choix du repas à l'étape Formule : GARDE la date déjà choisie.
  const setMealKeepDate = (st) => setForm((f) => ({
    ...f, slot_type: st,
    kiddoush_type: st === "seouda_chlichit" ? (seoudaTypes[0]?.id || "libre") : "petit",
    custom_amount: "",
  }));
  const selectStandardDate = (d) => setForm((f) => ({ ...f, desired_date: d, custom_slot_id: null }));
  const selectCustomSlot = (slot) => setForm((f) => ({
    ...f, custom_slot_id: slot.id, desired_date: slot.date,
    slot_type: slot.slot_type || "kiddoush",
    kiddoush_type: "libre", custom_amount: String(slot.suggested_amount || ""),
  }));
  // Étape 2 (Date) valide ? Une date standard OU une occasion spéciale choisie.
  const dateReady = isCustom || !!form.desired_date;
  // Le repas choisi est-il déjà réservé pour cette date ? (conflit étape Formule)
  const slotConflict = !isCustom && !!form.desired_date && slotTakenFor(form.desired_date, form.slot_type);
  // Étape 3 (Formule) valide ? Formule choisie (ou montant > 0 si libre/occasion)
  // ET pas de conflit de créneau.
  const typeReady = isCustom
    ? parseFloat(form.custom_amount) > 0
    : !slotConflict && !!form.kiddoush_type &&
      (form.kiddoush_type !== "libre" || parseFloat(form.custom_amount) > 0);

  // ── Two-field contact recognition (email + phone independently) ────────
  const emailTimer = React.useRef(null);
  const phoneTimer = React.useRef(null);

  const _doCheck = async (params) => {
    try {
      // Même reconnaissance que les pages de don, limitée à CETTE synagogue.
      // Sans code, elle ne dit que « on vous connaît déjà » : jamais le nom ni
      // rien de la fiche (décision d'Avi, 30/09/2026). Le nom revient après
      // la vérification d'identité, obligatoire ici (voir plus bas).
      const r = await axios.get(`${PUBLIC_API}/campaigns/public/recognize-donor`, { params });
      setContactCheck(r.data);
    } catch {
      // Panne de la reconnaissance → on ouvre quand même les champs
      // (« contact d'abord » ne doit jamais bloquer une réservation).
      setContactCheck({ found: false });
    }
    finally { setContactExamined(true); setContactChecking(false); }
  };

  // Identité VÉRIFIÉE d'un donateur reconnu → sa fiche revient (avec le
  // jeton) : nom et contact manquant sont remplis, il peut les corriger.
  useEffect(() => {
    if (!verificationToken || !contactCheck?.found || !selectedOrg) return;
    const methode = contactCheck.match_method === "phone" ? "phone" : "email";
    let annule = false;
    (async () => {
      try {
        const d = await chargerFicheProuvee({
          orgId: selectedOrg.id, methode,
          contact: methode === "phone" ? form.phone : form.email,
          jeton: verificationToken,
        });
        if (annule || !d) return;
        setForm((f) => ({
          ...f,
          first_name: d.first_name || f.first_name,
          last_name: d.last_name || f.last_name,
          email: f.email || d.email || "",
          phone: f.phone || d.phone || "",
        }));
      } catch { /* pas bloquant : le donateur saisit lui-même */ }
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationToken]);

  const handleEmailChange = (v) => {
    set("email", v);
    setContactCheck(null);
    if (emailTimer.current) clearTimeout(emailTimer.current);
    if (!v.trim() || !v.includes("@") || !selectedOrg) return;
    setContactChecking(true);
    emailTimer.current = setTimeout(() =>
      _doCheck({ org_id: selectedOrg.id, email: v.trim() }), 500);
  };

  const handlePhoneChange = (v) => {
    set("phone", v);
    // Don't overwrite an existing email-based recognition
    if (contactCheck?.found) return;
    setContactCheck(null);
    if (phoneTimer.current) clearTimeout(phoneTimer.current);
    if (v.replace(/\D/g, "").length < 8 || !selectedOrg) return;
    setContactChecking(true);
    phoneTimer.current = setTimeout(() =>
      _doCheck({ org_id: selectedOrg.id, phone: v.trim() }), 500);
  };

  const submit = async () => {
    // Le nom vient TOUJOURS du formulaire : saisi, ou rempli après la
    // vérification d'identité (la reconnaissance ne le donne plus).
    const finalFirstName = form.first_name.trim();
    const finalLastName  = form.last_name.trim();
    if (!finalFirstName || !finalLastName || !form.desired_date || !selectedOrg) {
      toast.error("Remplissez tous les champs obligatoires (prénom, nom, date).");
      return;
    }
    // Recognised donors don't need to retype their contact — the backend
    // resolves the donor via the recognition payload (first_name/last_name
    // or the email/phone used during contact-check) and uses their stored
    // contact info to deliver the confirmation link.
    const recognized = !!contactCheck?.found;
    if (!recognized && !form.email.trim() && !form.phone.trim()) {
      toast.error("Renseignez votre email ou téléphone pour recevoir le lien de confirmation.");
      return;
    }
    if (!identityVerified) {
      toast.error("Vérifiez votre identité avant de valider votre réservation.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${PUBLIC_API}/kiddoush/reserve`, {
        organization_id: selectedOrg.id,
        first_name: finalFirstName,
        last_name:  finalLastName,
        email: form.email.trim() || "",
        phone: form.phone.trim() || "",
        desired_date: form.desired_date,
        kiddoush_type: form.kiddoush_type,
        slot_type: form.slot_type,
        custom_slot_id: form.custom_slot_id || undefined,
        custom_amount: (isCustom || form.kiddoush_type === "libre") ? parseFloat(form.custom_amount || "0") : undefined,
        occasion: form.occasion,
        dedication: form.dedication,
        guests_estimate: parseInt(form.guests_estimate || "0"),
        notes: form.notes,
        verification_token: verificationToken || undefined,
      });
      setNeedsApproval(!!data?.needs_approval);
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la réservation.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#F4F3EF" }}>
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-lg">
        {needsApproval ? (
          <>
            <div className="h-16 w-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-5">
              <span className="text-3xl" role="img" aria-label="sablier">⏳</span>
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2">Demande en attente de validation</h2>
            <p className="text-stone-600 text-sm mb-3">
              Vous avez déjà <strong>3 réservations Kiddoush à venir</strong>. Votre nouvelle demande doit être validée par la synagogue avant d'être confirmée — vous serez prévenu(e) dès qu'elle est traitée.
            </p>
          </>
        ) : form.email ? (
          <>
            <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5">
              <Mail className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2">Vérifiez vos emails !</h2>
            <p className="text-stone-600 text-sm mb-3">
              Un email de confirmation vous a été envoyé à <strong>{form.email}</strong>.
            </p>
          </>
        ) : (
          <>
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-5">
              <Phone className="h-8 w-8 text-blue-500" />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2">Vérifiez vos SMS !</h2>
            <p className="text-stone-600 text-sm mb-3">
              Un SMS de confirmation vous a été envoyé au <strong>{form.phone}</strong>.
            </p>
          </>
        )}
        <p className="text-stone-500 text-sm mb-4">
          <strong>Cliquez sur le lien</strong> pour valider votre demande auprès de{" "}
          <strong>{selectedOrg?.name}</strong>.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-left mb-6 text-xs text-amber-700">
          <p className="font-medium mb-1">Le lien est valable 24h.</p>
          <p>Sans confirmation, votre demande sera automatiquement annulée et la date redeviendra disponible.</p>
        </div>
        <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-medium" style={{ background: "#0D1B33" }}>
          <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "#F4F3EF" }}>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><TeroumaLogo textColor="#0D1B33" /></Link>
          <Link to="/login" className="text-sm text-stone-500 hover:text-stone-900">Connexion</Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-[11px] uppercase mb-4"
             style={{ color: "#B8962E", letterSpacing: "0.28em", fontWeight: 500 }}>
            Réservation en ligne
          </p>
          <h1 className="text-4xl font-light text-stone-900 mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Réserver un Kiddoush
          </h1>
          <p className="text-stone-500 text-sm max-w-md mx-auto">Offrez un Kiddouch à votre communauté pour célébrer un événement. Votre nom sera annoncé à la synagogue.</p>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-3 mb-10">
          {["Organisation", "Date", "Formule", "Vos coordonnées"].map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 text-sm ${step > i + 1 ? "text-emerald-600" : step === i + 1 ? "font-semibold text-stone-900" : "text-stone-400"}`}>
                <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${step > i + 1 ? "bg-emerald-100 text-emerald-700" : step === i + 1 ? "bg-stone-900 text-white" : "bg-stone-200 text-stone-500"}`}>
                  {step > i + 1 ? "✓" : i + 1}
                </div>
                <span className="hidden sm:inline">{s}</span>
              </div>
              {i < 3 && <ChevronRight className="h-4 w-4 text-stone-300" />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-sm border border-stone-100">

          {/* Step 1: Choose org */}
          {step === 1 && (
            <div>
              <h2 className="text-lg font-semibold text-stone-900 mb-2">Choisissez votre synagogue</h2>
              <p className="text-sm text-stone-500 mb-6">Sélectionnez l'organisation auprès de laquelle vous souhaitez réserver.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {orgs.map(o => (
                  <button key={o.id} onClick={() => setSelectedOrg(o)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${selectedOrg?.id === o.id ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-400"}`}>
                    <p className="font-medium text-sm text-stone-900">{o.name}</p>
                    <div className="flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3 text-stone-400" /><span className="text-xs text-stone-500">{o.city}</span></div>
                  </button>
                ))}
                {orgs.length === 0 && !orgsError && <p className="text-sm text-stone-400 col-span-2 text-center py-8">Chargement des organisations…</p>}
                {orgs.length === 0 && orgsError && <p className="text-sm text-rose-500 col-span-2 text-center py-8">Impossible de charger les organisations. Vérifiez votre connexion et rechargez la page.</p>}
              </div>
              <div className="flex justify-end mt-6">
                <button onClick={() => setStep(2)} disabled={!selectedOrg}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-medium disabled:opacity-40 transition-all" style={{ background: "#0D1B33" }}>
                  Continuer <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Date + type */}
          {step === 2 && (
            <div>
              <h2 className="text-lg font-semibold text-stone-900 mb-1">Choisissez la date</h2>
              <p className="text-sm text-stone-500 mb-6">
                Sélectionnez un Chabbat disponible{customSlots.length > 0 ? " ou une occasion spéciale" : ""}.
                {seoudaEnabled ? " Vous choisirez ensuite entre le kiddoush et la séouda chlichit." : ""}
              </p>

              {/* Occasions spéciales (créneaux personnalisés ouverts par la synagogue) */}
              {customSlots.length > 0 && (
                <div className="mb-6">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-3">Occasions spéciales</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {customSlots.map((s) => {
                      const selected = form.custom_slot_id === s.id;
                      const dt = new Date(s.date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });
                      return (
                        <button key={s.id} disabled={s.taken} onClick={() => selectCustomSlot(s)}
                          className={`text-left p-4 rounded-xl border-2 transition-all ${s.taken ? "cursor-not-allowed border-stone-200 bg-stone-50 opacity-60" : selected ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-300"}`}>
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-semibold text-sm text-stone-900">{s.label}</p>
                            <span className="text-[10px] uppercase tracking-wide text-stone-400">{s.slot_type === "seouda_chlichit" ? "Séouda" : "Kiddoush"}</span>
                          </div>
                          <p className="text-xs text-stone-500 mt-0.5 capitalize">{dt}</p>
                          {s.description && <p className="text-xs text-stone-500 mt-1">{s.description}</p>}
                          <p className="text-sm font-bold mt-1.5" style={{ color: "#B8962E" }}>
                            {s.suggested_amount ? `${s.suggested_amount} € suggéré` : "Montant libre"}
                          </p>
                          {s.taken && <p className="text-[10px] text-rose-400 mt-0.5">Réservé</p>}
                        </button>
                      );
                    })}
                  </div>
                  {isCustom && (
                    <button onClick={() => selectSlotType("kiddoush")} className="text-xs text-stone-500 underline mt-3">
                      ← Revenir à un Chabbat ordinaire
                    </button>
                  )}
                </div>
              )}

              {/* Date d'un Chabbat ORDINAIRE (masquée en mode occasion spéciale) */}
              {!isCustom && (
                <div className="mb-6">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-3">Date souhaitée (Chabbat)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {saturdays.map(d => {
                      const taken = dateUnavailable(d);
                      const selected = !isCustom && form.desired_date === d;
                      const date = new Date(d + "T12:00:00");
                      const label = date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
                      const parasha = parashiot[d];
                      return (
                        <button key={d} disabled={taken} onClick={() => selectStandardDate(d)}
                          className={`py-2 px-2.5 rounded-xl border-2 text-left transition-all ${taken ? "cursor-not-allowed border-stone-200 bg-stone-50" : selected ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 hover:border-stone-400"}`}>
                          <p className={`font-medium text-sm ${taken ? "text-stone-400" : ""}`}>{label}</p>
                          {parasha?.he && (
                            <p className={`text-[11px] mt-0.5 truncate font-medium ${selected ? "text-stone-200" : "text-stone-700"}`} style={{ fontFamily: "serif", direction: "rtl" }}>
                              {parasha.he}
                            </p>
                          )}
                          {parasha?.fr && (
                            <p className={`text-[10px] truncate ${selected ? "text-stone-300" : "text-stone-400"}`}>{parasha.fr}</p>
                          )}
                          {taken && <p className="text-[10px] text-rose-400 mt-0.5">Réservé</p>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-6">
                <button onClick={() => setStep(1)} className="px-5 py-2.5 rounded-xl border border-stone-300 text-stone-600 text-sm hover:bg-stone-50 transition-colors">
                  Retour
                </button>
                <button onClick={() => setStep(3)} disabled={!dateReady}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-medium disabled:opacity-40 transition-all" style={{ background: "#0D1B33" }}>
                  Continuer <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Formule / montant (page dédiée, juste avant les coordonnées) */}
          {step === 3 && (
            <div>
              <h2 className="text-lg font-semibold text-stone-900 mb-1">
                {isCustom ? "Votre participation" : "Votre réservation"}
              </h2>
              <p className="text-sm text-stone-500 mb-6">
                {isCustom
                  ? "Choisissez le montant de votre participation."
                  : seoudaEnabled
                    ? "Choisissez le repas, puis la formule."
                    : "Choisissez la formule pour votre kiddoush."}
              </p>

              {/* 2 réservations possibles AVANT le prix : kiddoush du Chabbat /
                  séouda chlichit (si la synagogue propose la séouda). */}
              {!isCustom && seoudaEnabled && (
                <div className="mb-6">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-3">Quelle réservation ?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => setMealKeepDate("kiddoush")}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${form.slot_type === "kiddoush" ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-300"}`}>
                      <p className="font-semibold text-sm text-stone-900">Kiddoush du Chabbat</p>
                      <p className="text-xs text-stone-500 mt-1">Après l'office du matin</p>
                    </button>
                    <button onClick={() => setMealKeepDate("seouda_chlichit")}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${form.slot_type === "seouda_chlichit" ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-300"}`}>
                      <p className="font-semibold text-sm text-stone-900">Séouda Chlichit</p>
                      <p className="text-xs text-stone-500 mt-1">Le 3ᵉ repas, l'après-midi</p>
                    </button>
                  </div>
                  {slotConflict && (
                    <p className="text-xs text-rose-600 mt-3">
                      Ce créneau ({slotNoun.toLowerCase()}) est déjà réservé pour cette date.
                      Choisissez l'autre repas, ou revenez en arrière changer de date.
                    </p>
                  )}
                </div>
              )}

              {isCustom ? (
                /* Occasion spéciale : récap du créneau + proposition de montant */
                <div className="mb-6">
                  <div className="rounded-xl border-2 border-stone-900 bg-stone-50 p-4 mb-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm text-stone-900">
                        {customSlots.find(s => s.id === form.custom_slot_id)?.label || "Occasion spéciale"}
                      </p>
                      <span className="text-[10px] uppercase tracking-wide text-stone-400">{form.slot_type === "seouda_chlichit" ? "Séouda" : "Kiddoush"}</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5 capitalize">
                      {form.desired_date ? new Date(form.desired_date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" }) : ""}
                    </p>
                  </div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Votre montant (€)</label>
                  <input type="number" min="1" step="1" inputMode="numeric"
                    value={form.custom_amount} onChange={e => set("custom_amount", e.target.value)}
                    placeholder="Montant proposé"
                    className="w-full sm:w-48 px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                  <p className="text-[10px] text-stone-400 mt-1">Acceptez le montant suggéré ou proposez le vôtre — la synagogue confirmera.</p>
                </div>
              ) : (
                <div className="mb-6">
                  {seoudaEnabled && (
                    <p className="text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-3">
                      Formule {form.slot_type === "seouda_chlichit" ? "séouda" : "kiddoush"}
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {currentTypes.map(t => (
                      <button key={t.id} onClick={() => set("kiddoush_type", t.id)}
                        className={`text-left p-4 rounded-xl border-2 transition-all ${form.kiddoush_type === t.id ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-300"}`}>
                        <p className="font-semibold text-sm text-stone-900">{t.label}</p>
                        <p className="text-lg font-bold mt-1" style={{ color: "#B8962E" }}>{t.price} €</p>
                        <p className="text-xs text-stone-500 mt-1">{t.desc}</p>
                      </button>
                    ))}
                    {/* Montant libre — le donateur choisit le montant (admin confirme) */}
                    <button onClick={() => set("kiddoush_type", "libre")}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${form.kiddoush_type === "libre" ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-300"}`}>
                      <p className="font-semibold text-sm text-stone-900">Montant libre</p>
                      <p className="text-lg font-bold mt-1" style={{ color: "#B8962E" }}>Au choix</p>
                      <p className="text-xs text-stone-500 mt-1">Indiquez le montant souhaité</p>
                    </button>
                  </div>
                  {form.kiddoush_type === "libre" && (
                    <div className="mt-3">
                      <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Montant (€)</label>
                      <input
                        type="number" min="1" step="1" inputMode="numeric"
                        value={form.custom_amount}
                        onChange={e => set("custom_amount", e.target.value)}
                        placeholder="Ex : 150"
                        className="w-full sm:w-48 px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Occasion */}
              <div className="mb-4">
                <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Occasion (optionnel)</label>
                <input value={form.occasion} onChange={e => set("occasion", e.target.value)} placeholder="ex : Bar Mitsvah de Moshé Cohen, Mazel Tov famille Lévy…"
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
              </div>

              <div className="mb-4">
                <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Dédicace (annoncée à la synagogue)</label>
                <input value={form.dedication} onChange={e => set("dedication", e.target.value)} placeholder="ex : En l'honneur de la Bat Mitsvah de Sarah"
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
              </div>

              <div className="flex justify-between mt-6">
                <button onClick={() => setStep(2)} className="px-5 py-2.5 rounded-xl border border-stone-300 text-stone-600 text-sm hover:bg-stone-50 transition-colors">
                  Retour
                </button>
                <button onClick={() => setStep(4)} disabled={!typeReady}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-medium disabled:opacity-40 transition-all" style={{ background: "#0D1B33" }}>
                  Continuer <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Contact */}
          {step === 4 && (
            <div>
              <h2 className="text-lg font-semibold text-stone-900 mb-1">Vos coordonnées</h2>
              <p className="text-sm text-stone-500 mb-5">Renseignez votre email et/ou téléphone pour recevoir le lien de confirmation.</p>

              {/* ── Hint avant reconnaissance ─────────────────────────────── */}
              {!contactCheck?.found && (
                <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-stone-50 border border-stone-200">
                  <UserCheck className="h-3.5 w-3.5 text-stone-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-stone-600 leading-relaxed">
                    <strong className="text-stone-800">Déjà donateur ?</strong> Saisissez votre <strong>email</strong> ou
                    votre <strong>téléphone</strong> ci-dessous : si nous vous connaissons,
                    vous serez reconnu et vos informations seront pré-remplies.
                  </p>
                </div>
              )}

              {/* ── Badge de reconnaissance ─────────────────────────────── */}
              {contactCheck?.found && (
                <div className="mb-5 rounded-xl px-3 py-2.5 border bg-emerald-50 border-emerald-200">
                  <div className="flex items-start gap-2.5">
                    <UserCheck className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      {/* Jamais le nom sans preuve d'identité (30/09/2026). */}
                      <p className="text-sm font-semibold text-emerald-800">
                        ✓ On vous connaît déjà
                      </p>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <p className="text-xs text-emerald-600 inline-flex items-center gap-1">
                          {contactCheck.match_method === "email" ? (
                            <><Mail className="h-3 w-3" /> Reconnu par votre email</>
                          ) : contactCheck.match_method === "phone" ? (
                            <><Phone className="h-3 w-3" /> Reconnu par votre téléphone</>
                          ) : (
                            <>Compte retrouvé sur la plateforme</>
                          )}
                          {contactCheck.in_org && (
                            <span className="text-emerald-500">· déjà donateur</span>
                          )}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setForm(prev => ({...prev, first_name: "", last_name: "", email: "", phone: ""}));
                            setContactCheck(null);
                            setVerificationToken("");
                          }}
                          className="text-xs text-emerald-600 hover:text-emerald-800 underline shrink-0"
                        >
                          Recommencer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Message contextuel — champs manquants dans le dossier */}
              {contactCheck?.found && (contactCheck.missing_fields || []).length > 0 && (
                <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
                  <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-800 leading-relaxed">
                    <strong>Quelques infos manquent dans votre dossier.</strong>{" "}
                    {(contactCheck.missing_fields || []).includes("phone") && !((contactCheck.missing_fields || []).includes("email")) &&
                      "Votre numéro de téléphone n'est pas enregistré — l'ajouter permet de recevoir les rappels par SMS."}
                    {(contactCheck.missing_fields || []).includes("email") && !((contactCheck.missing_fields || []).includes("phone")) &&
                      "Votre email n'est pas enregistré — l'ajouter permet de recevoir les confirmations de réservation."}
                    {(contactCheck.missing_fields || []).includes("phone") && (contactCheck.missing_fields || []).includes("email") &&
                      "Email et téléphone manquent — les renseigner facilite le suivi de votre réservation."}
                    {((contactCheck.missing_fields || []).includes("first_name") || (contactCheck.missing_fields || []).includes("last_name")) &&
                      !((contactCheck.missing_fields || []).includes("phone")) && !((contactCheck.missing_fields || []).includes("email")) &&
                      "Merci de compléter votre nom pour finaliser votre réservation."}
                  </div>
                </div>
              )}

              {/* ── Champs Prénom / Nom ────────────────────────────────── */}
              {/* Cachés tant qu'aucun email/téléphone n'a été examiné
                  (« contact d'abord », comme la page de fête), puis toujours
                  visibles : la reconnaissance ne donne plus le nom — il est
                  saisi, ou rempli après la vérification d'identité. */}
              {contactExamined && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Prénom *</label>
                    <input value={form.first_name} onChange={e => set("first_name", e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Nom *</label>
                    <input value={form.last_name} onChange={e => set("last_name", e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                  </div>
                </div>
              )}

              {/* Visibility rules:
                  - If donor NOT recognized yet → show both fields (they may use either to be recognized)
                  - If donor IS recognized:
                      * email field appears ONLY when email is in missing_fields (= not yet on file)
                      * phone field appears ONLY when phone is in missing_fields (= not yet on file)
                  This avoids asking returning donors to re-type information already on file. */}
              {(() => {
                const recognized = !!contactCheck?.found;
                const missing = contactCheck?.missing_fields || [];
                const showEmail = !recognized || missing.includes("email");
                const showPhone = !recognized || missing.includes("phone");

                return (
                  <>
                    {/* ── Email ─────────────────────────────────────────────── */}
                    {showEmail && (
                      <div className="mb-3">
                        <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">
                          Email
                          {recognized && missing.includes("email") && (
                            <span className="normal-case tracking-normal text-amber-600 ml-1">— à compléter</span>
                          )}
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                          <input
                            type="email"
                            value={form.email}
                            onChange={e => handleEmailChange(e.target.value)}
                            placeholder="votre@email.com"
                            className={`w-full pl-9 pr-9 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 transition-colors ${
                              contactCheck?.found && form.email ? "border-emerald-300 bg-emerald-50/20" : "border-stone-200"
                            }`}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {contactChecking && !form.phone && <Loader2 className="h-4 w-4 animate-spin text-stone-400" />}
                            {!contactChecking && contactCheck?.found && form.email && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Téléphone ──────────────────────────────────────────── */}
                    {showPhone && (
                      <div className="mb-5">
                        <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">
                          Téléphone
                          {recognized && missing.includes("phone") && (
                            <span className="normal-case tracking-normal text-amber-600 ml-1">— à compléter</span>
                          )}
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                          <input
                            type="tel"
                            value={form.phone}
                            onChange={e => handlePhoneChange(e.target.value)}
                            placeholder="+33 6 12 34 56 78"
                            className={`w-full pl-9 pr-9 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 transition-colors ${
                              contactCheck?.found && form.phone ? "border-emerald-300 bg-emerald-50/20" : "border-stone-200"
                            }`}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {contactChecking && !form.email && <Loader2 className="h-4 w-4 animate-spin text-stone-400" />}
                            {!contactChecking && contactCheck?.found && form.phone && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                          </div>
                        </div>
                        {!recognized && (
                          <p className="text-[10px] text-stone-400 mt-1">
                            Si vous renseignez les deux, la confirmation sera envoyée par email.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Soft confirmation message when nothing needs to be filled */}
                    {recognized && !showEmail && !showPhone && (
                      <div className="mb-5 px-4 py-3 rounded-xl bg-emerald-50/50 border border-emerald-100 text-sm text-emerald-700">
                        ✓ Toutes vos coordonnées sont déjà enregistrées. La confirmation sera envoyée par email.
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="mb-4">
                <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Nombre d'invités estimé</label>
                <input type="number" min="0" value={form.guests_estimate} onChange={e => set("guests_estimate", e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
              </div>
              <div className="mb-6">
                <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Notes pour l'organisation</label>
                <textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 resize-none" />
              </div>

              {/* Summary */}
              <div className="bg-stone-50 rounded-xl p-4 mb-6 text-sm">
                <p className="font-semibold text-stone-900 mb-2">Récapitulatif</p>
                <p className="text-stone-600">{selectedOrg?.name} · {selectedOrg?.city}</p>
                <p className="text-stone-600">{form.desired_date ? new Date(form.desired_date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" }) : "—"}</p>
                <p className="text-stone-600">
                  {form.slot_type === "seouda_chlichit" ? "Séouda Chlichit" : "Kiddoush"}
                  {isCustom
                    ? ` · ${customSlots.find(s => s.id === form.custom_slot_id)?.label || "Occasion spéciale"}`
                    : form.kiddoush_type === "libre"
                      ? " · Montant libre"
                      : ` · ${currentTypes.find(t => t.id === form.kiddoush_type)?.label || ""}`}
                </p>
                <p className="font-bold mt-2 text-lg" style={{ color: "#B8962E" }}>
                  {(isCustom || form.kiddoush_type === "libre")
                    ? (parseFloat(form.custom_amount) || 0)
                    : (currentTypes.find(t => t.id === form.kiddoush_type)?.price || 0)} €
                </p>
                <p className="text-xs text-stone-400">Paiement à régler après confirmation de l'organisation</p>
              </div>

              {/* Unified identity verification — required for ALL reservations */}
              <div className="mb-4">
                <IdentityVerification
                  organizationId={selectedOrg?.id}
                  email={form.email}
                  phone={form.phone}
                  firstName={form.first_name}
                  recognized={contactCheck?.found ? contactCheck : null}
                  onVerified={(token) => setVerificationToken(token)}
                  context="kiddoush"
                />
              </div>

              <div className="flex justify-between">
                <button onClick={() => setStep(3)} className="px-5 py-2.5 rounded-xl border border-stone-300 text-stone-600 text-sm hover:bg-stone-50 transition-colors">
                  Retour
                </button>
                <button
                  onClick={submit}
                  disabled={(() => {
                    if (submitting) return true;
                    if (!form.email.trim() && !form.phone.trim()) return true;
                    if (!identityVerified) return true;
                    if (!form.first_name.trim() || !form.last_name.trim()) return true;
                    return false;
                  })()}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  style={{ background: "#0D1B33" }}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" />
                   : !identityVerified ? <ShieldAlert className="h-4 w-4" />
                   : <CheckCircle2 className="h-4 w-4" />}
                  {!identityVerified
                    ? "Vérifiez votre identité d'abord"
                    : "Envoyer la demande"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
